import React, { useState, useRef, useCallback, useMemo } from "react";
import {
  Button,
  CircularProgress,
  Alert,
  Box,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import { CloudUpload, Download, Close } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { sendMessageToAgent } from "../../store/userSlice";
import { v4 as uuid } from "uuid";
import styles from "./chatwindow.module.css";

// Constants
const ALLOWED_FILE_EXTENSIONS = [".xlsx"] as const;
const TEMPLATE_FILENAME = "manpower-cost-template.xlsx";

interface ManpowerCostFormProps {
  hideSubtitle?: boolean;
  templateUrl?: string;
}

export default function ManpowerCostForm({
  hideSubtitle = false,
  templateUrl: propTemplateUrl,
}: ManpowerCostFormProps = {}): JSX.Element {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Local state
  const [projectName, setProjectName] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Memoized selectors
  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );
  const agentLoading = useSelector((state: RootState) => state.user.loading);

  // Memoized session ID extraction
  const sessionId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("sessionId");
  }, []);

  // Use only the prop template URL, no fallback
  const templateUrl = propTemplateUrl;

  // Check if template is available
  const hasTemplate = Boolean(templateUrl);

  // Memoized form validation
  const isFormValid = useMemo(
    () => Boolean(projectName.trim() && serialNumber.trim() && uploadedFile),
    [projectName, serialNumber, uploadedFile],
  );

  // Download template handler
  const handleDownloadTemplate = useCallback(() => {
    if (!templateUrl) return;

    const link = document.createElement("a");
    link.href = templateUrl;
    link.download = TEMPLATE_FILENAME;
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [templateUrl]);

  // File validation helper
  const validateAndSetFile = useCallback((file: File) => {
    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    if (
      !ALLOWED_FILE_EXTENSIONS.includes(
        fileExtension as (typeof ALLOWED_FILE_EXTENSIONS)[number],
      )
    ) {
      setError(
        "Only .xlsx files are accepted. Please upload the completed template.",
      );
      return;
    }

    setUploadedFile(file);
    setError(null);
  }, []);

  // File input handler
  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        validateAndSetFile(file);
      }
    },
    [validateAndSetFile],
  );

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        validateAndSetFile(file);
      }
    },
    [validateAndSetFile],
  );

  // Remove file handler
  const handleRemoveFile = useCallback(() => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  // Browse file handler
  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Form submission handler
  const handleSubmit = useCallback(async () => {
    // Validation
    if (!projectName.trim()) {
      setError("Please enter a project name");
      return;
    }
    if (!serialNumber.trim()) {
      setError("Please enter a serial number");
      return;
    }
    if (!uploadedFile) {
      setError("Please upload the completed resource plan template");
      return;
    }

    if (!currentAgent) {
      setError("Agent configuration not found");
      return;
    }

    try {
      setError(null);
      setIsSubmitted(true);

      const questionId = uuid();
      const timestamp = new Date().toISOString();

      // Prepare the message payload with form data
      // The backend will process the uploaded Excel file and extract resource plan details
      const payload = {
        agentId: currentAgent.id,
        message: JSON.stringify({
          project_name: projectName.trim(),
          serial_number: serialNumber.trim(),
          action: "calculate_manpower_cost",
        }),
        questionId,
        timestamp,
        agentEndpoint: currentAgent.endpoint,
        sessionId,
        files: [uploadedFile], // Excel file with resource plan
      };

      dispatch(sendMessageToAgent(payload));
    } catch (err) {
      console.error("Error submitting manpower cost request:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to submit request. Please try again.";
      setError(errorMessage);
      setIsSubmitted(false);
    }
  }, [
    projectName,
    serialNumber,
    uploadedFile,
    currentAgent,
    sessionId,
    dispatch,
  ]);

  return (
    <Box className={styles.manpowerFormContainer}>
      {/* Header */}
      <Box className={styles.manpowerFormHeader}>
        <Box className={styles.manpowerFormHeaderIcon}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#00A3A3">
            <circle cx="12" cy="12" r="3" />
          </svg>
        </Box>
        <Typography
          variant="subtitle2"
          className={styles.manpowerFormHeaderText}
        >
          MANPOWER COST CALCULATOR
        </Typography>
      </Box>

      {!hideSubtitle && (
        <Typography variant="body2" className={styles.manpowerFormSubtitle}>
          Great — first the request details, then the resource plan.
        </Typography>
      )}

      {/* Error display */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Form Fields */}
      <Box className={styles.manpowerFormFields}>
        {/* Project Name */}
        <Box className={styles.manpowerFormField}>
          <Typography variant="caption" className={styles.manpowerFormLabel}>
            PROJECT NAME <span className={styles.manpowerFormRequired}>*</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g. Regional Logistics Modernisation"
            value={projectName}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= 256) {
                setProjectName(value);
              }
            }}
            disabled={agentLoading || isSubmitted}
            variant="outlined"
            size="small"
            className={styles.manpowerFormInput}
            inputProps={{ maxLength: 256 }}
            helperText={`${projectName.length}/256 characters`}
          />
        </Box>

        {/* Serial Number */}
        <Box className={styles.manpowerFormField}>
          <Typography variant="caption" className={styles.manpowerFormLabel}>
            SERIAL NUMBER <span className={styles.manpowerFormRequired}>*</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g. SR-4471"
            value={serialNumber}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= 256) {
                setSerialNumber(value);
              }
            }}
            disabled={agentLoading || isSubmitted}
            variant="outlined"
            size="small"
            className={styles.manpowerFormInput}
            inputProps={{ maxLength: 256 }}
            helperText={`${serialNumber.length}/256 characters`}
          />
        </Box>

        {/* Resource Plan */}
        <Box className={styles.manpowerFormField}>
          <Box className={styles.manpowerFormLabelRow}>
            <Typography variant="caption" className={styles.manpowerFormLabel}>
              RESOURCE PLAN{" "}
              <span className={styles.manpowerFormRequired}>*</span>
            </Typography>
            <Tooltip
              title={!hasTemplate ? "No template found" : ""}
              arrow
              disableInteractive
            >
              <span>
                <Button
                  startIcon={<Download />}
                  onClick={handleDownloadTemplate}
                  disabled={agentLoading || isSubmitted || !hasTemplate}
                  className={styles.manpowerDownloadButton}
                  size="small"
                >
                  Download Template
                </Button>
              </span>
            </Tooltip>
          </Box>

          <Typography variant="caption" className={styles.manpowerFormHint}>
            Fill this in, then upload it below
          </Typography>

          {/* File Upload Area */}
          <Box
            className={`${styles.manpowerFileUpload} ${isDragging ? styles.manpowerFileUploadDragging : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={
              !uploadedFile && !isSubmitted ? handleBrowseClick : undefined
            }
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={handleFileSelect}
              style={{ display: "none" }}
              disabled={agentLoading || isSubmitted}
            />

            {uploadedFile ? (
              <Box className={styles.manpowerUploadedFile}>
                <Box className={styles.manpowerUploadedFileInfo}>
                  <CloudUpload className={styles.manpowerUploadedFileIcon} />
                  <Box>
                    <Typography
                      variant="body2"
                      className={styles.manpowerUploadedFileName}
                    >
                      {uploadedFile.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      className={styles.manpowerUploadedFileSize}
                    >
                      {(uploadedFile.size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile();
                  }}
                  disabled={agentLoading || isSubmitted}
                  className={styles.manpowerRemoveFileButton}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box className={styles.manpowerFileUploadPrompt}>
                <CloudUpload className={styles.manpowerUploadIcon} />
                <Typography
                  variant="body2"
                  className={styles.manpowerUploadText}
                >
                  Drag and drop your completed template here, or click to browse
                </Typography>
                <Typography
                  variant="caption"
                  className={styles.manpowerUploadHint}
                >
                  Only Excel (.xlsx) files are supported. Please upload a valid
                  .xlsx template.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Submit Button */}
      <Box className={styles.manpowerFormSubmit}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isFormValid || agentLoading || isSubmitted}
          className={styles.manpowerSubmitButton}
          endIcon={
            agentLoading ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            )
          }
        >
          {agentLoading ? "Processing" : "Submit Request"}
        </Button>
      </Box>
    </Box>
  );
}
